<template>
  <div
    class="upload_wrapper"
    :class="{
      upload_wrapper_h: currentType == 'h',
      upload_wrapper_v: currentType == 'v',
      upload_wrapper_none: (formState.fileUrls || formState.localPreviewUrl) && !isLoading,
    }"
    @click="handleUploadClick"
  >
    <template v-if="!formState.fileUrls && !formState.localPreviewUrl">
      <img src="@/assets/images/upload_license.svg" alt="Upload Icon" class="upload_icon" />
      <div class="upload_text">Click to upload</div>
    </template>
    <div v-else class="file_preview">
      <div v-if="!isLoading" class="mask"></div>
      <div v-if="!isLoading" class="re_upload_wrapper" @click.stop="handleUploadClick">
        <img src="@/assets/images/re_upload.svg" alt="Upload Icon" class="upload_icon" />
        <div class="re_upload_text">Re-upload</div>
      </div>
      <img
        v-if="formState.type.indexOf('image') > -1"
        :src="formState.localPreviewUrl || formState.fileUrls"
        class="img_preview"
        :class="{ img_vertical: currentType == 'v' }"
      />
      <video
        id="currentVideoPlayer"
        v-if="formState.type.indexOf('video') > -1"
        :poster="formState.tempCoverUrl || getVideoCover(formState.fileUrls)"
        oncontextmenu="return false;"
        :src="formState.localPreviewUrl || formState.fileUrls"
        class="video_player"
        @loadedmetadata="getVideoType()"
      />
    </div>
    <a-spin
      class="avatar_loading"
      :spinning="isLoading"
      :indicator="indicator"
      :tip="
        isCompressing
          ? 'Compressing ' + compressProgress + '%'
          : uploadProgress > 0
          ? 'Uploading ' + uploadProgress + '%'
          : 'Uploading...'
      "
    />
    <input type="file" ref="fileInput" :accept="props.accept" style="display:none" @change="handleFileChange" />
  </div>
</template>

<script setup>
import { LoadingOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { reactive, ref, defineEmits, defineProps, h, defineExpose } from 'vue';

import { upload, createVodUpload, completeVodUpload, fetchVodPlayInfo } from '@/api/common.js';
import { compressImage } from '@/utils/imageCompressor';
import { isVodId, playSrc, putFileToVodOss, waitForVodPlayInfo } from '@/utils/vod.js';

const indicator = h(LoadingOutlined, {
  style: {
    fontSize: '24px',
    color: '#ffffff',
  },
  spin: true,
});
const props = defineProps({
  accept: {
    type: String,
  },
});

const emit = defineEmits(['on-upload', 'on-upload-with-cover']);

const isLoading = ref(false);
const isCompressing = ref(false);
const uploadProgress = ref(0);
const compressProgress = ref(0);
const fileInput = ref(null);
const handleUploadClick = () => {
  fileInput.value && fileInput.value.click();
};
const formState = reactive({
  fileUrls: '',
  type: '',
  tempCoverUrl: '', // 视频本地临时封面
  localPreviewUrl: '', // 图片本地 ObjectURL 即时预览
});
const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  if (props.accept == 'video/*' && file.type.indexOf('video') == -1) {
    message.error('Please upload the video in the correct format.');
    return;
  }
  if (props.accept == 'image/*' && file.type.indexOf('image') == -1) {
    message.error('Please upload the image in the correct format.');
    return;
  }
  if (props.accept == 'video/*' && file.size / (1024 * 1024) > 300) {
    message.error('Please upload a video within 300MB.');
    return;
  }
  // 统一清理旧状态（re-upload 时清掉上一次的本地预览和远程 URL）
  if (formState.localPreviewUrl) {
    URL.revokeObjectURL(formState.localPreviewUrl);
    formState.localPreviewUrl = '';
  }
  if (formState.tempCoverUrl) {
    URL.revokeObjectURL(formState.tempCoverUrl);
    formState.tempCoverUrl = '';
  }
  formState.fileUrls = '';
  formState.type = '';
  currentType.value = null;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', 1);

  if (file.type.indexOf('video') > -1) {
    isLoading.value = true;
    isCompressing.value = false;
    compressProgress.value = 0;
    uploadProgress.value = 0;
    formState.type = file.type;
    formState.localPreviewUrl = URL.createObjectURL(file);
    generateTempCover(file);
    uploadVideoToVod(file);
    return;
  } else if (file.type.indexOf('image') > -1) {
    // 立即生成本地预览 URL，让缩略图秒显
    formState.localPreviewUrl = URL.createObjectURL(file);
    formState.type = file.type;
    getImageType(); // 用本地图判断横竖比例

    isLoading.value = true;
    isCompressing.value = true;
    compressProgress.value = 0;
    uploadProgress.value = 0;

    // Start Image Compression
    compressImage(file, (p) => {
      compressProgress.value = p;
    })
      .then((result) => {
        isCompressing.value = false;
        uploadProgress.value = 0;
        const uploadFormData = new FormData();
        uploadFormData.append('file', result.blob, result.fallback ? file.name : 'compressed.jpg');
        uploadFormData.append('folder', 1);

        if (result.fallback) {
          console.log('[Upload] Using original file as fallback');
        }

        return upload(uploadFormData, (p) => {
          uploadProgress.value = p;
        });
      })
      .then((res) => {
        uploadProgress.value = 0;
        if (res.code === 0) {
          formState.fileUrls = res.data.fileUrl;
          // 预加载远程 OSS 图，加载完再关闭 loading + revoke 本地 URL
          const remoteImg = new Image();
          remoteImg.onload = () => {
            isLoading.value = false;
            if (formState.localPreviewUrl) {
              URL.revokeObjectURL(formState.localPreviewUrl);
              formState.localPreviewUrl = '';
            }
            emit('on-upload', res.data.fileUrl, file.type);
          };
          remoteImg.onerror = () => {
            // 远程加载失败也不关 loading 太久，直接切换
            isLoading.value = false;
            if (formState.localPreviewUrl) {
              URL.revokeObjectURL(formState.localPreviewUrl);
              formState.localPreviewUrl = '';
            }
            emit('on-upload', res.data.fileUrl, file.type);
          };
          remoteImg.src = res.data.fileUrl;
        } else {
          isLoading.value = false;
          message.error(res.msg);
        }
      })
      .catch((err) => {
        isLoading.value = false;
        isCompressing.value = false;
        uploadProgress.value = 0;
        if (formState.localPreviewUrl) {
          URL.revokeObjectURL(formState.localPreviewUrl);
          formState.localPreviewUrl = '';
        }
        formState.type = '';
        console.error(err);
        message.error('Compression or upload failed.');
      });
  } else {
    isLoading.value = true;
    isCompressing.value = false;
    uploadProgress.value = 0;
    upload(formData, (p) => {
      uploadProgress.value = p;
    })
      .then((res) => {
        isLoading.value = false;
        uploadProgress.value = 0;
        if (res.code === 0) {
          formState.fileUrls = res.data.fileUrl;
          formState.type = file.type;
          emit('on-upload', res.data.fileUrl, file.type);
        } else {
          message.error(res.msg);
        }
      })
      .catch(() => {
        isLoading.value = false;
        isCompressing.value = false;
        uploadProgress.value = 0;
      });
  }
};
const handleInitFormState = (url) => {
  formState.fileUrls = url || '';
  if (formState.localPreviewUrl) {
    URL.revokeObjectURL(formState.localPreviewUrl);
    formState.localPreviewUrl = '';
  }
  formState.tempCoverUrl = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  currentType.value = null;
  if (url) {
    if (isVodId(url)) {
      formState.type = 'video';
      fetchVodPlayInfo(url).then((res) => {
        if (res.code === 0 && res.data && res.data.playUrl) {
          formState.fileUrls = res.data.playUrl;
        }
      });
      return;
    }
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.mkv'];
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];

    const fileExtension = url.slice(((url.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
    if (videoExtensions.includes('.' + fileExtension)) {
      formState.type = 'video';
    } else if (imageExtensions.includes('.' + fileExtension)) {
      formState.type = 'image';
      getImageType();
    } else {
      formState.type = 'unknown';
    }
  }
};

const uploadVideoToVod = async (file) => {
  try {
    const authRes = await createVodUpload({
      fileName: file.name,
      title: file.name,
      fileSize: file.size,
    });
    if (authRes.code !== 0 || !authRes.data) {
      throw new Error(authRes.msg || 'Unable to create upload credential');
    }
    const auth = authRes.data;
    await putFileToVodOss(auth, file, (percent) => {
      uploadProgress.value = Math.min(99, Math.round(percent * 100));
    });
    const done = await completeVodUpload({
      videoId: auth.videoId,
      fileName: file.name,
      fileSize: file.size,
    });
    if (done.code !== 0) {
      throw new Error(done.msg || 'Unable to save video');
    }
    const play = await waitForVodPlayInfo(auth.videoId);
    uploadProgress.value = 100;
    isLoading.value = false;
    formState.type = 'video';
    if (play && play.playUrl) {
      formState.fileUrls = play.playUrl;
    }
    const cover = formState.tempCoverUrl || play?.coverUrl || '';
    emit('on-upload-with-cover', done.data.fileUrl, 'video/', cover, play?.playUrl || formState.localPreviewUrl);
  } catch (err) {
    isLoading.value = false;
    isCompressing.value = false;
    uploadProgress.value = 0;
    console.error(err);
    message.error(err.message || 'Video upload failed, please try again');
  }
};

const getVideoCover = (videoUrl) => {
  if (!videoUrl) return '';
  return videoUrl + '?x-oss-process=video/snapshot,t_1000,f_jpg,w_500,ar_auto';
};

// 从本地文件生成临时封面
const generateTempCover = (file) => {
  const video = document.createElement('video');
  video.src = URL.createObjectURL(file);
  video.muted = true;
  video.playsInline = true;

  video.addEventListener('loadeddata', () => {
    video.currentTime = video.duration > 1 ? 1 : 0.1;
  });

  video.addEventListener('seeked', () => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    formState.tempCoverUrl = canvas.toDataURL('image/jpeg', 0.8);
    URL.revokeObjectURL(video.src);
  });

  video.addEventListener('error', () => {
    URL.revokeObjectURL(video.src);
  });
};

const currentType = ref(null);
const getVideoType = () => {
  const videoElement = document.getElementById('currentVideoPlayer');
  // 弹窗关闭/表单重置后元素已卸载，但 pending 的 loadedmetadata 仍可能触发
  if (!videoElement) return;

  const width = videoElement.videoWidth;
  const height = videoElement.videoHeight;

  if (width > height) {
    currentType.value = 'h';
  } else {
    currentType.value = 'v';
  }
};

const getImageType = () => {
  const img = new Image();
  img.src = formState.localPreviewUrl || formState.fileUrls;
  img.onload = () => {
    if (img.width > img.height) {
      currentType.value = 'h';
    } else {
      currentType.value = 'v';
    }
  };
};

defineExpose({
  handleInitFormState,
});
</script>

<style lang="less">
.avatar_loading {
  .ant-spin-text {
    color: #fff;
    width: 100%;
    font-size: 16px;
    margin-top: 10px;
    text-align: center;
    z-index: 9999;
  }
}
</style>

<style lang="less" scoped>
.upload_wrapper {
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  border: 1px dashed rgba(5, 100, 79, 0.5);
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  cursor: pointer;
  flex-wrap: wrap;
  position: relative;
  width: 140px;
  height: 248px;
  &.upload_wrapper_h {
    width: 200px;
    height: 112.5px;
  }
  &.upload_wrapper_h_16_9 {
    width: 200px;
    height: 112.5px;
  }
  &.upload_wrapper_v {
    width: 140px;
    height: auto;
  }
  &.upload_wrapper_none {
    border: none;
    box-shadow: none;
  }
  .upload_text {
    width: 100%;
    font-size: 14px;
    color: #05644f;
    text-align: center;
    margin-top: 10px;
  }
  .file_preview {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 8px;
    .re_upload_wrapper {
      display: none;
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-wrap: wrap;
      align-content: center;
      color: #fff;
      z-index: 99;
      .re_upload_text {
        display: none;
        width: 100%;
        font-size: 16px;
        text-align: center;
        margin-top: 5px;
      }
      img {
        display: none;
      }
    }
    .video_player,
    .img_preview {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 8px;
    }
    .img_preview {
      object-fit: cover;
      &.img_vertical {
        object-fit: contain;
        background: #f5f5f5;
      }
    }
    .video_player {
      background: #000;
      display: block;
    }
    .mask {
      display: none;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 8px;
      background: linear-gradient(0deg, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75));
    }
    &:hover {
      .mask,
      .re_upload_text {
        display: block;
      }
      .re_upload_wrapper {
        display: flex;
        img {
          display: block;
        }
      }
    }
  }
  .avatar_loading {
    border-radius: 8px;
    position: absolute;
    top: 1px;
    left: 1px;
    right: 1px;
    bottom: 1px;
    display: flex;
    align-content: center;
    justify-content: center;
    cursor: pointer;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75));
    flex-wrap: wrap;
  }
}
</style>
